<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, capitalize, watch } from 'vue'
import { getGifQueryOptions } from '#features/gif-selection'
import type { GifAttachment } from '../model/attachment'

const { attachment, variant = 'regular' } = defineProps<{
  attachment: GifAttachment
  variant?: 'regular' | 'compact'
}>()

const gifQuery = useQuery(getGifQueryOptions(attachment.giphyId.toString()))
const gif = gifQuery.data

const previewUrl = computed(
  () => gif.value?.media_formats.nanowebppreview_transparent?.url ?? gif.value?.media_formats.preview?.url
)
const url = computed(() => gif.value?.media_formats.webp_transparent?.url ?? gif.value?.media_formats.mediumgif?.url)

const isFullImageLoaded = ref(false)
watch(
  gif,
  gif => {
    // If there is no preview for gif, just loading the full image
    if (gif && !previewUrl.value) isFullImageLoaded.value = true
  },
  { immediate: true }
)

const onFullImageLoad = () => (isFullImageLoaded.value = true)

const isPreviewShown = computed(() => !!gif && !isFullImageLoaded.value)

const fullImageStyle = computed(() => {
  if (!isFullImageLoaded.value) return { width: 0, height: 0 }
  return {}
})
</script>

<template>
  <div :class="$style.root">
    <img
      v-if="isPreviewShown"
      :class="[$style.image, $style['imageVariant' + capitalize(variant)]]"
      :src="previewUrl"
      :alt="gif?.title"
    />
    <img
      :class="[$style.image, $style['imageVariant' + capitalize(variant)]]"
      :style="fullImageStyle"
      :src="url"
      :alt="gif?.title"
      autoplay
      loop
      @load="onFullImageLoad"
    />
  </div>
</template>

<style lang="scss" module>
$regularHeight: 200px;
$compactHeight: 150px;

.root {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.rootVariantRegular {
  height: $regularHeight;
}

.rootVariantCompact {
  height: $compactHeight;
}

.image {
  height: 200px;
  max-width: 100%;
  object-fit: contain;
  object-position: left;
}

.imageVariantRegular {
  height: $regularHeight;
}

.imageVariantCompact {
  height: $compactHeight;
}
</style>
